// Deep Jyotisha context for tooltips — written for the curious learner
// Sources: Brihat Parashara Hora Shastra, Phaladeepika, classical pandit lineage
// The voice: as if a 40-year scholar is leaning in, sharing one essential insight.

export const GRAHA_LORE = {
  Surya: {
    deity: "Agni · Bhaskara",
    karaka: "Atman, father, government, kingship, vitality",
    rules: "Leo (Simha)",
    exalted: "Aries 10°",
    debilitated: "Libra 10°",
    day: "Sunday · Ravivara",
    metal: "Gold",
    gem: "Ruby (Manikya)",
    direction: "East",
    guna: "Sattva",
    element: "Fire",
    body: "Heart, eyes, bones, spinal column",
    friends: "Moon, Mars, Jupiter",
    enemies: "Venus, Saturn",
    mantra: "ॐ घृणि सूर्याय नमः · Om Ghrini Suryaya Namah",
    teaching:
      "Surya is not the ego; he is the soul's first witness — atmakaraka. A weak Sun does not mean weak personality, but a soul learning to find its own throne. Read by sign, by aspect, by Sunday's mood.",
  },
  Chandra: {
    deity: "Apas · Soma",
    karaka: "Manas (mind), mother, nourishment, the public, water",
    rules: "Cancer (Karka)",
    exalted: "Taurus 3°",
    debilitated: "Scorpio 3°",
    day: "Monday · Somavara",
    metal: "Silver",
    gem: "Pearl (Mukta)",
    direction: "Northwest",
    guna: "Sattva (waxing) · Tamas (waning)",
    element: "Water",
    body: "Mind, blood, breasts, lymph",
    friends: "Sun, Mercury",
    enemies: "None — the Moon is friend to all",
    mantra: "ॐ सोम सोमाय नमः · Om Som Somaya Namah",
    teaching:
      "In Vedic astrology your Moon — not your Sun — speaks first. The mind it carries IS the chart's emotional weather. A waxing Moon strengthens; a waning Moon turns the reader inward. Your Moon's nakshatra is your truest signature.",
  },
  Mangala: {
    deity: "Kartikeya · Skanda",
    karaka: "Energy, courage, brothers, real estate, surgery",
    rules: "Aries (Mesha) · Scorpio (Vrischika)",
    exalted: "Capricorn 28°",
    debilitated: "Cancer 28°",
    day: "Tuesday · Mangalavara",
    metal: "Copper",
    gem: "Red Coral (Praval)",
    direction: "South",
    guna: "Tamas",
    element: "Fire",
    body: "Muscles, bone marrow, blood, genitals",
    friends: "Sun, Moon, Jupiter",
    enemies: "Mercury",
    mantra: "ॐ अं अंगारकाय नमः · Om Am Angarakaya Namah",
    teaching:
      "Mangala is the warrior — but in Jyotisha his higher form is the disciplined ascetic. Manglik dosha is misread as fate; classical texts treat it as a call for partnership with conscious fire. Mars in the 10th makes a leader; in the 7th, asks for grace.",
  },
  Budha: {
    deity: "Vishnu · Narayana (in his form as messenger)",
    karaka: "Buddhi (intellect), speech, commerce, writing, friends",
    rules: "Gemini (Mithuna) · Virgo (Kanya)",
    exalted: "Virgo 15°",
    debilitated: "Pisces 15°",
    day: "Wednesday · Budhavara",
    metal: "Brass / Bronze",
    gem: "Emerald (Panna)",
    direction: "North",
    guna: "Rajas",
    element: "Earth",
    body: "Skin, nervous system, lungs, tongue",
    friends: "Sun, Venus",
    enemies: "Moon",
    mantra: "ॐ बुं बुधाय नमः · Om Bum Budhaya Namah",
    teaching:
      "Budha is youth, agility, the word made bridge. He takes the colour of the planet he sits with — alone he is a clear pane, conjoined he becomes a stained glass. The strongest writers, traders, teachers carry a clean Budha in the 3rd or 5th.",
  },
  Guru: {
    deity: "Brihaspati · the Devaguru",
    karaka: "Wisdom, dharma, children, husband (in a woman's chart), wealth",
    rules: "Sagittarius (Dhanu) · Pisces (Meena)",
    exalted: "Cancer 5°",
    debilitated: "Capricorn 5°",
    day: "Thursday · Guruvara",
    metal: "Gold",
    gem: "Yellow Sapphire (Pukhraj)",
    direction: "Northeast",
    guna: "Sattva",
    element: "Ether",
    body: "Liver, fat, hips, growth",
    friends: "Sun, Moon, Mars",
    enemies: "Mercury, Venus",
    mantra: "ॐ बृं बृहस्पतये नमः · Om Brim Brihaspataye Namah",
    teaching:
      "Guru is the most blessed graha. His aspect alone — without sitting in a house — can sanctify it. Read your Guru by sign and house: where Jupiter sits, the soul is already protected; where Jupiter aspects, the universe is patient.",
  },
  Shukra: {
    deity: "Bhrigu's son · Daityaguru (preceptor of the asuras)",
    karaka: "Love, wife (in a man's chart), beauty, vehicles, refinement, semen",
    rules: "Taurus (Vrishabha) · Libra (Tula)",
    exalted: "Pisces 27°",
    debilitated: "Virgo 27°",
    day: "Friday · Shukravara",
    metal: "Silver / Platinum",
    gem: "Diamond (Heera)",
    direction: "Southeast",
    guna: "Rajas",
    element: "Water",
    body: "Reproductive system, kidneys, throat, eyes",
    friends: "Mercury, Saturn",
    enemies: "Sun, Moon",
    mantra: "ॐ शुं शुक्राय नमः · Om Shum Shukraya Namah",
    teaching:
      "Shukra is the only graha to know the secret of mritasanjeevani — the bringing back of life. A strong Venus does not just give beauty; she gives the capacity for love that survives loss. Friday fasts, white foods, and acts of beauty please her.",
  },
  Shani: {
    deity: "Yamaraja's brother · son of Surya and Chhaya",
    karaka: "Time, karma, longevity, servants, masses, sorrow that teaches",
    rules: "Capricorn (Makara) · Aquarius (Kumbha)",
    exalted: "Libra 20°",
    debilitated: "Aries 20°",
    day: "Saturday · Shanivara",
    metal: "Iron · Steel",
    gem: "Blue Sapphire (Neelam)",
    direction: "West",
    guna: "Tamas",
    element: "Air",
    body: "Bones, joints, teeth, knees, chronic conditions",
    friends: "Mercury, Venus",
    enemies: "Sun, Moon, Mars",
    mantra: "ॐ शं शनैश्चराय नमः · Om Sham Shanaishcharaya Namah",
    teaching:
      "Shani is feared, but he is the most just. Sade Sati — his 7.5-year transit over the natal Moon — is not a curse; it is the soul's apprenticeship. He removes only what cannot stand. The discipline you keep on Saturdays is paid back as freedom in his return.",
  },
  Rahu: {
    deity: "The northern lunar node · the head of the asura Svarbhanu",
    karaka: "Obsession, foreigners, the unfamiliar, technology, the mass mind",
    rules: "Co-rules Aquarius",
    exalted: "Taurus / Gemini (school-dependent)",
    debilitated: "Scorpio / Sagittarius",
    day: "(no day) — strongest in dusk and shadow",
    metal: "Lead",
    gem: "Hessonite (Gomedha)",
    direction: "Southwest",
    guna: "Tamas",
    element: "Air (smoke)",
    body: "Skin disorders, neurological conditions, snake-bites",
    friends: "Mercury, Venus, Saturn",
    enemies: "Sun, Moon, Mars",
    mantra: "ॐ रां राहवे नमः · Om Ram Rahave Namah",
    teaching:
      "Rahu is not a planet — he is a shadow, a story, an appetite. He gives, dramatically, what the soul never had in its past life — and teaches by overreach. His house in your chart is where you are addicted to becoming. Mature him through service.",
  },
  Ketu: {
    deity: "The southern lunar node · the tail of Svarbhanu",
    karaka: "Moksha, isolation, mysticism, past-life mastery, sudden release",
    rules: "Co-rules Scorpio",
    exalted: "Scorpio / Sagittarius",
    debilitated: "Taurus / Gemini",
    day: "(no day) — strongest in dawn",
    metal: "Lead alloys",
    gem: "Cat's Eye (Lehsuniya)",
    direction: "(headless · no direction)",
    guna: "Tamas (clarifying)",
    element: "Fire (smoke)",
    body: "Joints, intuition, head, abdominal cysts",
    friends: "Mercury, Venus, Saturn",
    enemies: "Sun, Moon, Mars",
    mantra: "ॐ कें केतवे नमः · Om Kem Ketave Namah",
    teaching:
      "Ketu is the saint, the renunciate — what Rahu chases, Ketu has already left behind. His house is where you carry quiet mastery from a previous birth, but ALSO where you are easily disinterested. Honour him by refusing to grasp.",
  },
};

// 27 Nakshatras with rich classical detail
export const NAKSHATRA_LORE = {
  Ashwini: {
    deity: "Ashvini Kumars — the divine physicians",
    symbol: "Horse's head",
    pada_count: 4,
    range: "Aries 0°00' – 13°20'",
    gana: "Deva (godly)",
    yoni: "Horse · masculine",
    sound: "Chu, Che, Cho, La",
    nature: "Swift, healing, pioneering",
    quality: "Light · Laghu (movable)",
    teaching:
      "The first nakshatra — the chart's dawn. Ashwini natives are born to begin: surgeons, athletes, first-responders. Their gift is the ability to heal what others cannot reach. Their lesson is patience.",
  },
  Bharani: {
    deity: "Yama — the lord of dharma and death",
    symbol: "Yoni · the womb",
    pada_count: 4,
    range: "Aries 13°20' – 26°40'",
    gana: "Manushya (human)",
    yoni: "Elephant · masculine",
    sound: "Li, Lu, Le, Lo",
    nature: "Bearing, transformative, judging",
    quality: "Fierce · Ugra",
    teaching:
      "Bharani holds the threshold between life and death. These natives carry an old burden — and an old discernment. Strong Bharani gives moral clarity that others mistake for severity.",
  },
  Krittika: {
    deity: "Agni · the fire of perception",
    symbol: "Razor · flame",
    pada_count: 4,
    range: "Aries 26°40' – Taurus 10°00'",
    gana: "Rakshasa (demonic)",
    yoni: "Sheep · feminine",
    sound: "A, I, U, E",
    nature: "Sharp, cleansing, critical",
    quality: "Mixed · Mishra",
    teaching:
      "Krittika is the sword that cuts illusion. These natives see what others avoid. The fire here is not destructive — it is the alchemist's flame. Their lesson is to wield discernment without wounding.",
  },
  Rohini: {
    deity: "Brahma · Prajapati",
    symbol: "Chariot · banyan tree",
    pada_count: 4,
    range: "Taurus 10°00' – 23°20'",
    gana: "Manushya (human)",
    yoni: "Serpent · masculine",
    sound: "O, Va, Vi, Vu",
    nature: "Magnetic, fertile, creative",
    quality: "Fixed · Dhruva",
    teaching:
      "The Moon's most exalted ground. Rohini is the bride, the muse, the artist. Beauty wears no apology here. The lesson is non-attachment — Krishna's birth-star reminds us that even the most magnetic must let go.",
  },
  Mrigashira: {
    deity: "Soma — the elixir of immortality",
    symbol: "Deer's head",
    pada_count: 4,
    range: "Taurus 23°20' – Gemini 6°40'",
    gana: "Deva (godly)",
    yoni: "Serpent · feminine",
    sound: "Ve, Vo, Ka, Ki",
    nature: "Searching, gentle, restless",
    quality: "Soft · Mridu",
    teaching:
      "The seeker's nakshatra. Mrigashira natives chase a fragrance they cannot name. Excellent travellers, researchers, writers. Their burden is finishing — they would rather move than complete.",
  },
  Ardra: {
    deity: "Rudra — the storm-form of Shiva",
    symbol: "Tear · diamond",
    pada_count: 4,
    range: "Gemini 6°40' – 20°00'",
    gana: "Manushya (human)",
    yoni: "Dog · feminine",
    sound: "Ku, Gha, Nga, Chha",
    nature: "Dissolving, revelatory, intense",
    quality: "Sharp · Tikshna",
    teaching:
      "Ardra is the storm before clarity. Natives carry rage, brilliance, and a strange tenderness. Their lives often pivot through one cataclysm that becomes their greatest teacher. Mantra heals what therapy cannot.",
  },
  Punarvasu: {
    deity: "Aditi — the boundless mother",
    symbol: "Quiver of arrows · house",
    pada_count: 4,
    range: "Gemini 20°00' – Cancer 3°20'",
    gana: "Deva (godly)",
    yoni: "Cat · feminine",
    sound: "Ke, Ko, Ha, Hi",
    nature: "Returning, restoring, kind",
    quality: "Movable · Chara",
    teaching:
      "The 'return of the light.' Punarvasu natives have the rare gift of beginning again — divorces become friendships, failures become books, illnesses become teachers. Rama's nakshatra: the prince who returned.",
  },
  Pushya: {
    deity: "Brihaspati — guru of the gods",
    symbol: "Cow's udder · lotus",
    pada_count: 4,
    range: "Cancer 3°20' – 16°40'",
    gana: "Deva (godly)",
    yoni: "Sheep · masculine",
    sound: "Hu, He, Ho, Da",
    nature: "Nourishing, protective, dharmic",
    quality: "Light · Laghu",
    teaching:
      "Considered the most auspicious nakshatra of all 27. No marriages performed here, but everything else thrives. Pushya natives feed others — literally or symbolically. They make extraordinary teachers, doctors, mothers.",
  },
  Ashlesha: {
    deity: "The Nagas — serpent kings",
    symbol: "Coiled serpent",
    pada_count: 4,
    range: "Cancer 16°40' – 30°00'",
    gana: "Rakshasa (demonic)",
    yoni: "Cat · masculine",
    sound: "Di, Du, De, Do",
    nature: "Hypnotic, secretive, magical",
    quality: "Sharp · Tikshna",
    teaching:
      "Ashlesha is the embrace of the serpent — kundalini in its raw form. These natives can mesmerize and manipulate, heal and poison. Their integrity is their entire path. Tantric practice belongs here.",
  },
  Magha: {
    deity: "The Pitris — the ancestors",
    symbol: "Royal throne",
    pada_count: 4,
    range: "Leo 0°00' – 13°20'",
    gana: "Rakshasa (demonic)",
    yoni: "Rat · masculine",
    sound: "Ma, Mi, Mu, Me",
    nature: "Regal, ancestral, ceremonial",
    quality: "Fierce · Ugra",
    teaching:
      "Magha holds the lineage. These natives feel an inheritance — gift or burden — that is not wholly theirs. They thrive in tradition, ceremony, leadership. Honouring ancestors clears the entire chart.",
  },
  "P. Phalguni": {
    deity: "Bhaga — lord of bliss",
    symbol: "Front legs of a bed · hammock",
    pada_count: 4,
    range: "Leo 13°20' – 26°40'",
    gana: "Manushya (human)",
    yoni: "Rat · feminine",
    sound: "Mo, Ta, Ti, Tu",
    nature: "Pleasure, romance, leisure",
    quality: "Fierce · Ugra",
    teaching:
      "The honeymoon nakshatra. Purva Phalguni natives know — bone-deep — that life is meant to be enjoyed. Excellent in entertainment, design, hospitality. Lesson: rest is sacred, but indulgence is its own debt.",
  },
  "U. Phalguni": {
    deity: "Aryaman — patron of contracts and unions",
    symbol: "Back legs of a bed · figs",
    pada_count: 4,
    range: "Leo 26°40' – Virgo 10°00'",
    gana: "Manushya (human)",
    yoni: "Cow · masculine",
    sound: "Te, To, Pa, Pi",
    nature: "Constant, contractual, generous",
    quality: "Fixed · Dhruva",
    teaching:
      "Where Purva Phalguni begins the romance, Uttara completes the marriage. These natives keep their word. Excellent partners, executors of estates, NGO leaders. The most reliable nakshatra.",
  },
  Hasta: {
    deity: "Savitar — Surya in his creating form",
    symbol: "Hand · fist",
    pada_count: 4,
    range: "Virgo 10°00' – 23°20'",
    gana: "Deva (godly)",
    yoni: "Buffalo · feminine",
    sound: "Pu, Sha, Na, Tha",
    nature: "Skilful, dexterous, witty",
    quality: "Light · Laghu",
    teaching:
      "Hasta is the hand of the craftsman. Anything these natives touch — instrument, surgery, illustration, code — they can master. Their gift is also their risk: they become indispensable, then resentful.",
  },
  Chitra: {
    deity: "Vishvakarma — the divine architect",
    symbol: "Bright jewel · pearl",
    pada_count: 4,
    range: "Virgo 23°20' – Libra 6°40'",
    gana: "Rakshasa (demonic)",
    yoni: "Tiger · feminine",
    sound: "Pe, Po, Ra, Ri",
    nature: "Beautiful, illusory, designed",
    quality: "Soft · Mridu",
    teaching:
      "Chitra means 'the brilliant.' These natives draw eyes wherever they go — and not always benignly. Master architects, designers, performers. Lesson: the most beautiful surface needs the most rigorous foundation.",
  },
  Swati: {
    deity: "Vayu — the wind",
    symbol: "Sword · young shoot bending in wind",
    pada_count: 4,
    range: "Libra 6°40' – 20°00'",
    gana: "Deva (godly)",
    yoni: "Buffalo · masculine",
    sound: "Ru, Re, Ro, Ta",
    nature: "Independent, diplomatic, balanced",
    quality: "Movable · Chara",
    teaching:
      "Swati is the reed in the wind — independent, flexible, alone. These natives must learn that they BECOME stronger by bending, not breaking. Excellent diplomats, writers, traders. The most independent nakshatra.",
  },
  Vishakha: {
    deity: "Indra-Agni — the dual lords of will",
    symbol: "Triumphal arch · potter's wheel",
    pada_count: 4,
    range: "Libra 20°00' – Scorpio 3°20'",
    gana: "Rakshasa (demonic)",
    yoni: "Tiger · masculine",
    sound: "Ti, Tu, Te, To",
    nature: "Ambitious, focused, achieving",
    quality: "Mixed · Mishra",
    teaching:
      "Vishakha is the goal-bearer. These natives chase the summit — sometimes at cost. They reach what others abandon. Buddha was born under this star: lesson is to know when the goal itself is the cage.",
  },
  Anuradha: {
    deity: "Mitra — the friend",
    symbol: "Lotus · staff",
    pada_count: 4,
    range: "Scorpio 3°20' – 16°40'",
    gana: "Deva (godly)",
    yoni: "Deer · feminine",
    sound: "Na, Ni, Nu, Ne",
    nature: "Devotional, gathering, friendly",
    quality: "Soft · Mridu",
    teaching:
      "Anuradha is the cosmic friend. These natives gather communities, lead movements with warmth instead of force. Saturn rules them, Mitra blesses them — discipline through love. Travel transforms their lives.",
  },
  Jyeshtha: {
    deity: "Indra — king of the devas",
    symbol: "Earring · umbrella · talisman",
    pada_count: 4,
    range: "Scorpio 16°40' – 30°00'",
    gana: "Rakshasa (demonic)",
    yoni: "Deer · masculine",
    sound: "No, Ya, Yi, Yu",
    nature: "Eldest, protective, occult",
    quality: "Sharp · Tikshna",
    teaching:
      "The eldest sister, the chief, the boss. Jyeshtha natives carry authority before they earn it. They are protective to a fault. Their lesson: not every burden is theirs to lift, and the throne can be lonely.",
  },
  Mula: {
    deity: "Nirriti — goddess of dissolution",
    symbol: "Bunch of roots · tied roots",
    pada_count: 4,
    range: "Sagittarius 0°00' – 13°20'",
    gana: "Rakshasa (demonic)",
    yoni: "Dog · masculine",
    sound: "Ye, Yo, Bha, Bhi",
    nature: "Investigative, root-finding, ruthless",
    quality: "Sharp · Tikshna",
    teaching:
      "Mula means 'root.' These natives dig until they hit truth — and they pay for the digging. Master investigators, surgeons of the psyche, philosophers. The 'gandanta' boundary asks them to grieve before they grow.",
  },
  "P. Ashadha": {
    deity: "Apas — the cosmic waters",
    symbol: "Fan · winnowing basket",
    pada_count: 4,
    range: "Sagittarius 13°20' – 26°40'",
    gana: "Manushya (human)",
    yoni: "Monkey · masculine",
    sound: "Bhu, Dha, Pha, Dha",
    nature: "Invigorating, declarative, popular",
    quality: "Fierce · Ugra",
    teaching:
      "Purva Ashadha is the early victor — the rabble-rouser, the speaker who moves crowds. These natives win the first round. Their lesson: one battle is not the war.",
  },
  "U. Ashadha": {
    deity: "Vishvedevas — the universal gods",
    symbol: "Tusk of an elephant · throne",
    pada_count: 4,
    range: "Sagittarius 26°40' – Capricorn 10°00'",
    gana: "Manushya (human)",
    yoni: "Mongoose · feminine",
    sound: "Be, Bo, Ja, Ji",
    nature: "Final-victory, righteous, enduring",
    quality: "Fixed · Dhruva",
    teaching:
      "Where Purva Ashadha wins the round, Uttara wins the war — slowly, righteously, irrevocably. These natives are the long-distance champions. The most unbeatable nakshatra.",
  },
  Shravana: {
    deity: "Vishnu (in his listening form)",
    symbol: "Three footprints · ear",
    pada_count: 4,
    range: "Capricorn 10°00' – 23°20'",
    gana: "Deva (godly)",
    yoni: "Monkey · feminine",
    sound: "Ju, Je, Jo, Gha",
    nature: "Listening, learning, traditional",
    quality: "Movable · Chara",
    teaching:
      "Shravana means 'hearing.' These natives are born scholars and counsellors — they hear what is unsaid. Excellent in academia, diplomacy, music, journalism. The teacher's nakshatra.",
  },
  Dhanishta: {
    deity: "The eight Vasus — gods of abundance",
    symbol: "Flute · drum",
    pada_count: 4,
    range: "Capricorn 23°20' – Aquarius 6°40'",
    gana: "Rakshasa (demonic)",
    yoni: "Lion · feminine",
    sound: "Ga, Gi, Gu, Ge",
    nature: "Wealthy, rhythmic, prominent",
    quality: "Movable · Chara",
    teaching:
      "Dhanishta means 'wealthiest.' These natives are born to abundance — material, musical, social. They lead in business and rhythm. The drum and flute are their tools. Lesson: hoarding kills the gift.",
  },
  Shatabhisha: {
    deity: "Varuna — lord of cosmic waters",
    symbol: "Empty circle · 100 healers",
    pada_count: 4,
    range: "Aquarius 6°40' – 20°00'",
    gana: "Rakshasa (demonic)",
    yoni: "Horse · feminine",
    sound: "Go, Sa, Si, Su",
    nature: "Healing, secretive, eccentric",
    quality: "Movable · Chara",
    teaching:
      "Shatabhisha — 'the hundred healers.' Natives are mystics, doctors, astrologers, recluses. They heal in private and disappear from public life. The most secretive nakshatra.",
  },
  "P. Bhadrapada": {
    deity: "Aja Ekapada — one-footed serpent",
    symbol: "Front of a funeral cot · two-faced man",
    pada_count: 4,
    range: "Aquarius 20°00' – Pisces 3°20'",
    gana: "Manushya (human)",
    yoni: "Lion · masculine",
    sound: "Se, So, Da, Di",
    nature: "Burning, philosophical, polarising",
    quality: "Fierce · Ugra",
    teaching:
      "Purva Bhadrapada burns. These natives carry a dharmic intensity — saints and zealots, both. They see the funeral pyre at every wedding. Asceticism eases them.",
  },
  "U. Bhadrapada": {
    deity: "Ahir Budhnya — the serpent of the deep",
    symbol: "Back of a funeral cot · twin",
    pada_count: 4,
    range: "Pisces 3°20' – 16°40'",
    gana: "Manushya (human)",
    yoni: "Cow · feminine",
    sound: "Du, Tha, Jna, Da",
    nature: "Deep, peaceful, emancipating",
    quality: "Fixed · Dhruva",
    teaching:
      "The serpent of the deep — moksha is closest in this nakshatra. Natives carry an inward stillness others mistake for distance. Excellent astrologers, counsellors, monks. They liberate without speaking.",
  },
  Revati: {
    deity: "Pushan — the shepherd, protector of journeys",
    symbol: "Drum · fish",
    pada_count: 4,
    range: "Pisces 16°40' – 30°00'",
    gana: "Deva (godly)",
    yoni: "Elephant · feminine",
    sound: "De, Do, Cha, Chi",
    nature: "Final, nurturing, transitional",
    quality: "Soft · Mridu",
    teaching:
      "The 27th — the chart's twilight before rebirth. Revati natives are born nurturers and finishers — they walk others to the door. The Krishna-Sudama nakshatra: friendship, devotion, gentle endings.",
  },
};
